import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { playLottieOnce } from "./lib-lottie"

gsap.registerPlugin(ScrollTrigger)

/**
 * ACHIEVEMENT（SECTION 01）の数値セルの演出
 *
 * @description セルが画面に入ったら、数字をスロットのように回してから止め、
 *              同じセルのLottie（[data-lottie-once]）を1回だけ流す。
 *              数字は桁ごとに「0〜9を何周も積んだ帯」を作って上へ送る。
 *              送り量は yPercent（帯自身の高さに対する割合）で指定するので、
 *              vwで組んだ文字サイズが画面幅で変わっても位置がずれない。
 *
 *              動きを減らす設定のときは帯を作らず、元の数字のまま置いておく。
 */

/** JSから触るためのフック */
const STAT_HOOK = ".ach__stat"
const NUM_HOOK = "[data-ach-num]"
const LOTTIE_HOOK = "[data-lottie]"

/** 止まるまでに回す周回数 */
const LOOPS = 4

/** 1桁目が止まるまでの秒数 */
const DURATION = 1.2

/** 桁が1つ右へ行くごとに伸ばす秒数。左の桁から順に止まって見える */
const STAGGER = 0.12

/** 動きを減らす設定のユーザーには適用しない */
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

/**
 * 1桁ぶんの帯を作る
 * @param {number} target - 止めたい数字（0〜9）
 * @returns {HTMLElement} 帯（.ach__reelStrip）を持つ窓（.ach__reel）
 */
const buildReel = (target: number): HTMLElement => {
  const reel = document.createElement("span")

  // 「1」は字面が細く右に余白が空くので、SCSS側で詰められるよう目印を付ける
  reel.className = target === 1 ? "ach__reel type-one" : "ach__reel"

  const strip = document.createElement("span")
  strip.className = "ach__reelStrip"

  // 0〜9をLOOPS周ぶん積み、最後に目的の数字までを足す
  const length = LOOPS * 10 + target + 1
  for (let i = 0; i < length; i++) {
    const digit = document.createElement("span")
    digit.textContent = String(i % 10)
    strip.append(digit)
  }

  reel.append(strip)
  return reel
}

/**
 * 数字をスロットの帯に組み替える
 * @description 見た目は帯に置き換わるので、読み上げ用に元の数字を残しておく。
 * @param {HTMLElement} el - [data-ach-num] を持つ数字の要素
 * @returns {HTMLElement[]} 桁ごとの帯（左の桁から順）
 */
const buildNumber = (el: HTMLElement): HTMLElement[] => {
  const text = el.textContent?.trim() ?? ""
  if (!text) return []

  const strips: HTMLElement[] = []
  const reels = document.createDocumentFragment()

  Array.from(text).forEach((char) => {
    if (!/[0-9]/.test(char)) {
      // カンマなどは回さないが、帯と同じ行の高さに揃えないと縦位置がずれる
      const fixed = document.createElement("span")
      fixed.className = "ach__reelChar"
      fixed.textContent = char
      reels.append(fixed)
      return
    }

    const reel = buildReel(Number(char))
    reels.append(reel)
    strips.push(reel.firstElementChild as HTMLElement)
  })

  const label = document.createElement("span")
  label.className = "a11y-hidden"
  label.textContent = text

  const visual = document.createElement("span")
  visual.className = "ach__reels"
  visual.setAttribute("aria-hidden", "true")
  visual.append(reels)

  el.textContent = ""
  el.append(label, visual)

  return strips
}

/**
 * 帯を回して止める
 * @param {HTMLElement[]} strips - 桁ごとの帯
 * @returns {void}
 */
const spin = (strips: HTMLElement[]): void => {
  strips.forEach((strip, index) => {
    const length = strip.children.length
    if (length < 2) return

    gsap.fromTo(
      strip,
      { yPercent: 0 },
      {
        yPercent: (-(length - 1) / length) * 100,
        duration: DURATION + index * STAGGER,
        ease: "power4.out",
        overwrite: true,
      }
    )
  })
}

const stats = Array.from(document.querySelectorAll<HTMLElement>(STAT_HOOK))

stats.forEach((stat) => {
  const num = stat.querySelector<HTMLElement>(NUM_HOOK)
  const strips = num && !reduceMotion ? buildNumber(num) : []
  const lottie = stat.querySelector<HTMLElement>(LOTTIE_HOOK)

  if (!strips.length && !lottie) return

  ScrollTrigger.create({
    trigger: stat,
    start: "top 80%",
    // once: true は使わない（理由は src/js/module/comp-scroll-reveal.ts のコメント参照）
    onEnter: () => {
      spin(strips)
      if (lottie) void playLottieOnce(lottie)
    },
  })
})
